import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

// Loads the kit models (public/assets/models) and bakes each into one vertex-coloured
// BufferGeometry: palette textures are sampled into the vertices, material colours (or the species
// colours we give them) painted in, every part merged. The result draws with the same vertex-colour
// Lambert materials and InstancedMesh pools as the shapes it replaces, so nothing else changes.

const loader = new GLTFLoader();
loader.setMeshoptDecoder(MeshoptDecoder);
const cache = new Map(); // name -> Promise<BufferGeometry | null>

// A textured tree from the nature kit: { geometry, materials }. Group 0 of the geometry is the
// trunk and branches with their texture baked into the vertices (drawn with the shared tree
// material), group 1 the leaves as alpha-cut cards drawn with their own texture, tinted to the
// species. options: scale, tint (hex over the leaf texture), onGround.
export function loadTree(name, options = {}) {
  const key = 'tree:' + name + JSON.stringify(options);
  if (cache.has(key)) return cache.get(key);
  const promise = loader
    .loadAsync(`assets/models/${name}.glb`)
    .then((gltf) => bakeTree(gltf.scene, options))
    .catch((e) => {
      console.warn(`tree model ${name} did not load`, e);
      return null;
    });
  cache.set(key, promise);
  return promise;
}

// A float copy of an attribute: quantized (normalized Int16) positions from meshopt files would
// clamp to ±1 once a node's scale is applied to them in place.
function floatAttribute(attr) {
  const n = attr.count;
  const size = attr.itemSize;
  const out = new Float32Array(n * size);
  for (let i = 0; i < n; i++) for (let k = 0; k < size; k++) out[i * size + k] = attr.getComponent(i, k);
  return new THREE.BufferAttribute(out, size);
}

let BARK = null;
function bakeTree(root, { scale = 1, tint = null, onGround = true }) {
  root.updateMatrixWorld(true);
  const colour = new THREE.Color();
  const leafTint = new THREE.Color(tint || 0xffffff);
  const opaque = [];
  const leaves = [];
  let leafMap = null;
  root.traverse((o) => {
    if (!o.isMesh) return;
    const material = Array.isArray(o.material) ? o.material[0] : o.material;
    const isLeaf = /leaf|leaves/i.test(material.name) || material.alphaTest > 0;
    const g = o.geometry.index ? o.geometry.toNonIndexed() : o.geometry.clone();
    const pos = g.attributes.position;
    const n = pos.count;
    const col = new Float32Array(n * 3);
    const uv = new Float32Array(n * 2);
    const map = !isLeaf && material.map && material.map.image ? sampler(material.map) : null;
    const srcUv = g.attributes.uv;
    for (let i = 0; i < n; i++) {
      if (isLeaf) {
        colour.copy(leafTint);
        if (srcUv) {
          uv[i * 2] = srcUv.getX(i);
          uv[i * 2 + 1] = srcUv.getY(i);
        }
      } else if (map && srcUv) map(srcUv.getX(i), srcUv.getY(i), colour);
      else colour.set(material.color || 0xffffff);
      col[i * 3] = colour.r;
      col[i * 3 + 1] = colour.g;
      col[i * 3 + 2] = colour.b;
    }
    if (isLeaf && material.map) leafMap = material.map;
    const clean = new THREE.BufferGeometry();
    clean.setAttribute('position', floatAttribute(pos));
    clean.setAttribute('normal', floatAttribute(g.attributes.normal || pos));
    clean.setAttribute('color', new THREE.BufferAttribute(col, 3));
    clean.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    clean.applyMatrix4(o.matrixWorld);
    if (!g.attributes.normal) clean.computeVertexNormals();
    (isLeaf ? leaves : opaque).push(clean);
  });
  if (!opaque.length && !leaves.length) return null;
  const merged = mergeGeometries([...opaque, ...leaves], false);
  const opaqueCount = opaque.reduce((s, g) => s + g.attributes.position.count, 0);
  const leafCount = leaves.reduce((s, g) => s + g.attributes.position.count, 0);
  merged.clearGroups();
  if (opaqueCount) merged.addGroup(0, opaqueCount, 0);
  if (leafCount) merged.addGroup(opaqueCount, leafCount, 1);
  merged.scale(scale, scale, scale);
  merged.computeBoundingBox();
  if (onGround) merged.translate(0, -merged.boundingBox.min.y, 0);
  merged.computeBoundingSphere();
  if (!BARK) BARK = new THREE.MeshLambertMaterial({ vertexColors: true });
  const leafMaterial = new THREE.MeshLambertMaterial({ map: leafMap, alphaTest: 0.5, side: THREE.DoubleSide, vertexColors: true });
  return { geometry: merged, materials: [BARK, leafMaterial] };
}

// Samples a texture's image at (u, v) into a Color, through a canvas drawn once per image.
const pixelCache = new WeakMap();
function sampler(texture) {
  const image = texture.image;
  let data = pixelCache.get(image);
  if (!data) {
    const c = document.createElement('canvas');
    c.width = image.width;
    c.height = image.height;
    const g = c.getContext('2d', { willReadFrequently: true });
    g.drawImage(image, 0, 0);
    data = { w: image.width, h: image.height, px: g.getImageData(0, 0, image.width, image.height).data };
    pixelCache.set(image, data);
  }
  const flip = texture.flipY;
  return (u, v, out) => {
    u -= Math.floor(u);
    v -= Math.floor(v);
    const x = Math.min(data.w - 1, Math.floor(u * data.w));
    const y = Math.min(data.h - 1, Math.floor((flip ? 1 - v : v) * data.h));
    const k = (y * data.w + x) * 4;
    out.setRGB(data.px[k] / 255, data.px[k + 1] / 255, data.px[k + 2] / 255, THREE.SRGBColorSpace);
    return out;
  };
}

// name: a file in public/assets/models without .glb. options:
//   scale: metres per model unit; onGround: true shifts the model so its lowest point is y = 0;
//   colours: { materialName: hex } overriding a material's colour (a species' leaves and bark);
//   yaw: radians to turn the model so it faces +z like everything else in the game.
export function loadModel(name, options = {}) {
  const key = name + JSON.stringify(options);
  if (cache.has(key)) return cache.get(key);
  const promise = loader
    .loadAsync(`assets/models/${name}.glb`)
    .then((gltf) => bake(gltf.scene, options))
    .catch((e) => {
      console.warn(`model ${name} did not load`, e);
      return null;
    });
  cache.set(key, promise);
  return promise;
}

// A vehicle model split into its body and its wheels, so the wheels can turn:
//   { body, wheel, wheels: [{ x, y, z, front }], radius }
// body: the geometry without wheels, on the ground, facing +z; wheel: one wheel's geometry about its
// own axle (the kit's wheels are all one shape); wheels: where each axle sits in the body's frame,
// front ones flagged; radius in metres. Wheel nodes are the ones named wheel-* (Kenney's and ours).
// Null if the model has no wheels or did not load.
export function loadVehicle(name, options = {}) {
  const key = 'vehicle:' + name + JSON.stringify(options);
  if (cache.has(key)) return cache.get(key);
  const promise = loader
    .loadAsync(`assets/models/${name}.glb`)
    .then((gltf) => bakeVehicle(gltf.scene, options))
    .catch((e) => {
      console.warn(`vehicle model ${name} did not load`, e);
      return null;
    });
  cache.set(key, promise);
  return promise;
}

// Finds the paint of a body (its most common vertex colour) and turns it white, so each instance
// can be tinted its own colour; returns the paint it had.
function whitenPaint(geometry) {
  const col = geometry.attributes.color;
  const counts = new Map();
  const key = (i) => `${Math.round(col.getX(i) * 40)},${Math.round(col.getY(i) * 40)},${Math.round(col.getZ(i) * 40)}`;
  for (let i = 0; i < col.count; i++) counts.set(key(i), (counts.get(key(i)) || 0) + 1);
  // the paint is the most common colour with real saturation: white trim, glass and tyres are not
  let best = null;
  let bestN = 0;
  for (const [k, n] of counts) {
    const [r, g, b] = k.split(',').map((v) => Number(v) / 40);
    const sat = Math.max(r, g, b) - Math.min(r, g, b);
    if (sat < 0.18 || n < col.count * 0.03) continue;
    if (n > bestN) {
      bestN = n;
      best = k;
    }
  }
  if (!best) for (const [k, n] of counts) if (n > bestN) {
    bestN = n;
    best = k;
  }
  const paint = new THREE.Color();
  if (best) {
    const [r, g, b] = best.split(',').map((v) => Number(v) / 40);
    paint.setRGB(r, g, b);
  }
  // every shade of it goes white: the palette is sampled a little differently face to face
  for (let i = 0; i < col.count; i++) {
    const d = Math.abs(col.getX(i) - paint.r) + Math.abs(col.getY(i) - paint.g) + Math.abs(col.getZ(i) - paint.b);
    if (d < 0.3) col.setXYZ(i, 1, 1, 1);
  }
  col.needsUpdate = true;
  return paint;
}

function bakeVehicle(root, { scale = 1, yaw = 0, paintable = false }) {
  root.updateMatrixWorld(true);
  // a wheel node is a mesh, or a group of meshes when it has more than one material
  const isWheel = (o) => /^wheel/i.test(o.name);
  const underWheel = (o) => {
    for (let p = o; p && p !== root; p = p.parent) if (isWheel(p)) return true;
    return false;
  };
  const nodes = [];
  root.traverse((o) => {
    if ((o.isMesh || o.isGroup || o.isObject3D) && isWheel(o) && !underWheel(o.parent)) nodes.push(o);
  });
  if (!nodes.length) return null;
  const body = bake(root, { scale, onGround: false, yaw, filter: (o) => !underWheel(o) });
  if (!body) return null;
  // each wheel's axle is the middle of its own geometry, wherever the exporter left the node's
  // origin (Kenney pivots at the inner face, Blender bakes the vertices into the model's space);
  // the first wheel's geometry, centred on its axle, serves every wheel
  const up = new THREE.Vector3(0, 1, 0);
  const p = new THREE.Vector3();
  const c = new THREE.Vector3();
  let wheel = null;
  let radius = 0;
  const wheels = [];
  for (const o of nodes) {
    const g = bake(o, { scale, onGround: false, yaw, local: true });
    if (!g) continue;
    g.computeBoundingBox();
    g.boundingBox.getCenter(c);
    o.getWorldPosition(p);
    if (yaw) p.applyAxisAngle(up, yaw);
    p.multiplyScalar(scale).add(c);
    wheels.push({ x: p.x, y: p.y, z: p.z, front: false });
    if (!wheel) {
      g.translate(-c.x, -c.y, -c.z);
      g.computeBoundingBox();
      g.computeBoundingSphere();
      wheel = g;
      radius = (g.boundingBox.max.y - g.boundingBox.min.y) / 2;
    }
  }
  if (!wheel || !wheels.length) return null;
  const paint = paintable ? whitenPaint(body) : null;
  // the ground is wherever the lowest thing (body or a wheel's bottom) sits
  body.computeBoundingBox();
  let ground = body.boundingBox.min.y;
  for (const w of wheels) ground = Math.min(ground, w.y - radius);
  body.translate(0, -ground, 0);
  body.computeBoundingSphere();
  const midZ = wheels.reduce((s, w) => s + w.z, 0) / wheels.length;
  for (const w of wheels) {
    w.y -= ground;
    w.front = w.z > midZ;
  }
  return { body, wheel, wheels, radius, paint };
}

function bake(root, { scale = 1, onGround = true, colours = {}, yaw = 0, filter = null, local = false }) {
  root.updateMatrixWorld(true);
  const parts = [];
  const colour = new THREE.Color();
  root.traverse((o) => {
    if (!o.isMesh) return;
    if (filter && !filter(o)) return;
    const g = o.geometry.index ? o.geometry.toNonIndexed() : o.geometry.clone();
    const pos = g.attributes.position;
    const n = pos.count;
    const col = new Float32Array(n * 3);
    const material = Array.isArray(o.material) ? o.material[0] : o.material;
    const override = colours[material.name];
    const map = !override && material.map && material.map.image ? sampler(material.map) : null;
    const uv = g.attributes.uv;
    for (let i = 0; i < n; i++) {
      if (map && uv) map(uv.getX(i), uv.getY(i), colour);
      else colour.set(override || material.color || 0xffffff);
      col[i * 3] = colour.r;
      col[i * 3 + 1] = colour.g;
      col[i * 3 + 2] = colour.b;
    }
    const clean = new THREE.BufferGeometry();
    clean.setAttribute('position', floatAttribute(pos));
    clean.setAttribute('normal', floatAttribute(g.attributes.normal || pos));
    clean.setAttribute('color', new THREE.BufferAttribute(col, 3));
    // local: the part's own frame (a wheel about its axle) instead of its place in the model
    if (local) {
      if (o !== root) clean.applyMatrix4(o.matrix);
    } else clean.applyMatrix4(o.matrixWorld);
    if (!g.attributes.normal) clean.computeVertexNormals();
    parts.push(clean);
  });
  if (!parts.length) return null;
  const merged = mergeGeometries(parts);
  if (yaw) merged.rotateY(yaw);
  merged.scale(scale, scale, scale);
  merged.computeBoundingBox();
  if (onGround) merged.translate(0, -merged.boundingBox.min.y, 0);
  merged.computeBoundingSphere();
  return merged;
}
